import { useMutation, useQuery, useQueryClient } from "react-query"
import * as routingApi from "./routingApi"
import { notification } from "antd"
import { PropertyGeolocationUpdateRecord } from "./RouteAdd/routeAddModel"
import { invalidateEventQueries } from '../ScheduleManagement/scheduleQueries'
import { hasValue } from '../../helpers/nullable'

export const useRoutingDataQuery = () => {
  return useQuery(['routes'], {
    queryFn: () => routingApi.getRoutesData(),
    refetchOnWindowFocus: false
  })
}

export const useRouteByIdQuery = (routeId: number | undefined | null) => {
  return useQuery(['routeById'], {
    queryFn: () => routingApi.getRouteById(routeId!),
    refetchOnWindowFocus: false,
    enabled: hasValue(routeId) && routeId !== 0
  })
}

export const useCrewQuery = () => {
  return useQuery(['crew'], {
    queryFn: () => routingApi.loadCrew(),
    refetchOnWindowFocus: false
  })
}

export const useSalesPeopleQuery = () => {
  return useQuery(['salesPeople'], {
    queryFn: () => routingApi.getSalesPeople(),
    refetchOnWindowFocus: false
  })
}

export const useJobsForCreateRouteQuery = () => {
  return useQuery(['jobsForCreateRoute'], {
    queryFn: () => routingApi.getJobsForCreateRoute(),
    refetchOnWindowFocus: false
  })
}

export const useRouteJobsQuery = (routeId: number) => {
  return useQuery(['routeJobs', routeId], {
    queryFn: () => routingApi.getRouteJobs(routeId),
    refetchOnWindowFocus: false
  })
}

export const useAllRouteJobsQuery = () => {
  return useQuery(['allRouteJobs'], {
    queryFn: () => routingApi.getAllRouteJobs(),
    refetchOnWindowFocus: false
  })
}

export const useSaveRouteActiveMutation = () => {
  const queryClient = useQueryClient()
  return useMutation(({ routeId, active }: { routeId: number, active: boolean }) => routingApi.saveRouteActive(routeId, active), {
    onSuccess: () => {
      queryClient.invalidateQueries(['routes'])
      queryClient.invalidateQueries(['routeById'])
      queryClient.invalidateQueries(['schedulingJobs'])
      queryClient.invalidateQueries(['scheduleCrew'])
      invalidateEventQueries(queryClient)
    },
    onError: () => {
      notification.error({
        message: 'There was a problem setting the route active.'
      })
    }
  })
}

export const useDeleteRouteMutation = () => {
  const queryClient = useQueryClient()
  return useMutation(({ routeId }: { routeId: number }) => routingApi.deleteRoute(routeId), {
    onSuccess: () => {
      queryClient.invalidateQueries(['routes'])
      queryClient.invalidateQueries(['schedulingJobs'])
      queryClient.invalidateQueries(['scheduleCrew'])
      invalidateEventQueries(queryClient)
    },
    onError: () => {
      notification.error({
        message: 'There was a problem deleting the route.'
      })
    }
  })
}

export const useDuplicateRouteMutation = () => {
  const queryClient = useQueryClient()
  return useMutation(({ routeId }: { routeId: number }) => routingApi.duplicateRoute(routeId), {
    onSuccess: () => {
      queryClient.invalidateQueries(['routes'])
    },
    onError: () => {
      notification.error({
        message: 'There was a problem duplicating the route.'
      })
    }
  })
}

export const useCreateRouteMutation = () => {
  const queryClient = useQueryClient()
  return useMutation((request: routingApi.CreateRouteRecordRequest) => routingApi.createRoute(request), {
    onSuccess: () => {
      queryClient.invalidateQueries(['routes'])
    },
    onError: () => {
      notification.error({
        message: 'There was a problem creating the route.'
      })
    }
  })
}

export const useRouteSchedulesQuery = () => {
  return useQuery(['routeSchedules'], {
    queryFn: () => routingApi.getRouteSchedules(),
    refetchOnWindowFocus: false
  })
}

export const useRouteScheduleByIdQuery = (routeId: number) => {
  return useQuery(['routeSchedule', routeId], {
    queryFn: () => routingApi.getRouteScheduleByRouteId(routeId),
    refetchOnWindowFocus: false,
    enabled: !!routeId
  })
}

export const useUpdateJobPropertyGeolocationMutation = () => {
  const queryClient = useQueryClient()
  return useMutation((request: PropertyGeolocationUpdateRecord) => routingApi.updateJobPropertyGeolocation(request), {
    onSuccess: () => {
      queryClient.invalidateQueries(['jobsForCreateRoute'])
      invalidateEventQueries(queryClient)
    },
    onError: () => {
      notification.error({
        message: 'There was a problem updating the job property geolocation.'
      })
    }
  })
}

export const useUpdateJobPropertyGeolocationMutationNoInvalidate = () => {
  return useMutation((request: PropertyGeolocationUpdateRecord) => routingApi.updateJobPropertyGeolocation(request))
}

export const useUpdateRouteMutation = () => {
  const queryClient = useQueryClient()
  return useMutation((request: routingApi.Route) => routingApi.updateRoute(request), {
    onSuccess: () => {
      queryClient.invalidateQueries(['routes'])
      queryClient.invalidateQueries(['routeById'])
      queryClient.invalidateQueries(['schedulingJobs'])
      queryClient.invalidateQueries(['scheduleCrew'])
      invalidateEventQueries(queryClient)
    },
    onError: () => {
      notification.error({
        message: 'There was a problem updating the route.'
      })
    }
  })
}

export const useRegenerateRouteMutation = () => {
  const queryClient = useQueryClient()
  return useMutation((routeId: number) => routingApi.regenerateRoute(routeId), {
    onSuccess: () => {
      queryClient.invalidateQueries(['routes'])
      queryClient.invalidateQueries(['routeById'])
      queryClient.invalidateQueries(['routeSchedules'])
      queryClient.invalidateQueries(['schedulingJobs'])
      queryClient.invalidateQueries(['scheduleCrew'])
      invalidateEventQueries(queryClient)
    },
    onError: () => {
      notification.error({
        message: 'There was a problem regenerating the route.'
      })
    }
  })
}

export const useInsertUpdateRouteJobsMutation = () => {
  const queryClient = useQueryClient()
  return useMutation(({ routeId, request }: { routeId: number, request: routingApi.RouteJob[] }) => routingApi.insertUpdateRouteJobs(routeId, request), {
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['routeJobs', variables.routeId])
      queryClient.invalidateQueries(['routes'])
      queryClient.invalidateQueries(['routeById'])
      queryClient.invalidateQueries(['schedulingJobs'])
      queryClient.invalidateQueries(['scheduleCrew'])
      queryClient.invalidateQueries(['allRouteJobs'])
      invalidateEventQueries(queryClient)
    },
    onError: () => {
      notification.error({
        message: 'There was a problem updating the route jobs.'
      })
    }
  })
}

export const useDeactivateJobMutation = () => {
  const queryClient = useQueryClient()
  return useMutation((jobId: number) => routingApi.deactivateJob(jobId), {
    onMutate: async (jobId) => {
      await queryClient.cancelQueries(['jobsForCreateRoute'])
      const previousJobs = queryClient.getQueryData<routingApi.AddJobsRecord[]>(['jobsForCreateRoute'])
      queryClient.setQueryData<routingApi.AddJobsRecord[]>(['jobsForCreateRoute'], (old) =>
        old?.map(job => job.jobId === jobId ? { ...job, active: false } : job) ?? []
      )
      return { previousJobs }
    },
    onError: (_err, _jobId, context) => {
      if (context?.previousJobs) {
        queryClient.setQueryData(['jobsForCreateRoute'], context.previousJobs)
      }
      notification.error({
        message: 'There was a problem marking the job as inactive.'
      })
    }
  })
}